import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import api from '../services/api';

const GCC_LOCATIONS = [
  { country: 'Kuwait', cities: ['Kuwait City', 'Salmiya', 'Hawalli', 'Farwaniya', 'Ahmadi', 'Jahra'] },
  { country: 'Saudi Arabia', cities: ['Riyadh', 'Jeddah', 'Dammam', 'Khobar', 'Mecca', 'Medina'] },
  { country: 'UAE', cities: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'RAK', 'Fujairah'] },
  { country: 'Qatar', cities: ['Doha', 'Al Wakrah', 'Al Khor', 'Lusail'] },
  { country: 'Bahrain', cities: ['Manama', 'Riffa', 'Muharraq', 'Hamad Town'] },
  { country: 'Oman', cities: ['Muscat', 'Salalah', 'Sohar', 'Nizwa'] },
];

const SKILL_OPTIONS = [
  { value: 'Photography', icon: '📸' },
  { value: 'Videography', icon: '🎬' },
  { value: 'Graphic Design', icon: '🎨' },
  { value: 'Social Media', icon: '📱' },
  { value: 'Web Development', icon: '💻' },
  { value: 'Content Writing', icon: '✍️' },
  { value: 'Modelling', icon: '🧍' },
  { value: 'Studio Rental', icon: '🎙️' },
  { value: 'Brand Strategy', icon: '💡' },
  { value: 'Animation', icon: '🎞️' },
  { value: 'UI/UX Design', icon: '🖌️' },
  { value: 'SEO & Marketing', icon: '📈' },
];

const TOTAL_STEPS = 5;

export default function ProviderSetupPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const avatarInputRef = useRef(null);

  const [form, setForm] = useState({
    companyName: user?.companyName || '',
    bio: user?.bio || '',
    skills: user?.skills || [],
    location: user?.location || '',
    hourlyRate: user?.hourlyRate || '',
    avatar: null,
    avatarPreview: user?.avatar || null,
  });

  const toggleSkill = (skill) => {
    setForm(f => ({
      ...f,
      skills: f.skills.includes(skill) ? f.skills.filter(s => s !== skill) : [...f.skills, skill]
    }));
  };

  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm(f => ({ ...f, avatar: file, avatarPreview: URL.createObjectURL(file) }));
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      if (form.companyName) fd.append('companyName', form.companyName);
      if (form.bio) fd.append('bio', form.bio);
      if (form.skills.length) form.skills.forEach(s => fd.append('skills', s));
      if (form.location) fd.append('location', form.location);
      if (form.hourlyRate) fd.append('hourlyRate', form.hourlyRate);
      if (form.avatar) fd.append('avatar', form.avatar);
      const { data } = await api.put('/users/me', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser(data);
      navigate('/home', { replace: true });
    } catch {
      setSaving(false);
    }
  };

  const next = () => setStep(s => Math.min(s + 1, TOTAL_STEPS));
  const back = () => setStep(s => Math.max(s - 1, 1));

  const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col px-5 pt-14 pb-10">

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-dark-subtext text-xs">Step {step} of {TOTAL_STEPS}</span>
          <button onClick={() => navigate('/home', { replace: true })} className="text-dark-subtext text-xs underline underline-offset-2">
            Skip for now
          </button>
        </div>
        <div className="h-1 bg-dark-surface rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #6C5CE7, #a855f7)' }} />
        </div>
      </div>

      {/* ── Step 1: Welcome + Company Name ────────────────────────── */}
      {step === 1 && (
        <div className="flex-1 flex flex-col">
          <div className="mb-8">
            <span className="text-4xl mb-4 block">👋</span>
            <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
              Welcome to Findr, {user?.fullName?.split(' ')[0]}!
            </h1>
            <p className="text-dark-subtext text-sm leading-relaxed">
              Let's set up your provider profile so clients can find and book you. It only takes a minute.
            </p>
          </div>
          <div className="flex flex-col gap-4 flex-1">
            <div>
              <label className="text-dark-subtext text-xs font-medium uppercase tracking-wide mb-2 block">
                What's your company or studio name?
              </label>
              <input
                value={form.companyName}
                onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))}
                placeholder="e.g. Ali Creative Studio"
                className="w-full bg-dark-surface border border-dark-border rounded-2xl px-4 py-3.5 text-dark-text text-base outline-none focus:border-brand-purple placeholder:text-dark-muted"
                autoFocus
              />
              <p className="text-dark-subtext text-xs mt-1.5">Leave blank to use your full name</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Skills ──────────────────────────────────────────── */}
      {step === 2 && (
        <div className="flex-1 flex flex-col">
          <div className="mb-6">
            <span className="text-4xl mb-4 block">🛠️</span>
            <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
              What do you specialise in?
            </h1>
            <p className="text-dark-subtext text-sm">Pick all that apply — you can always change this later.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 flex-1">
            {SKILL_OPTIONS.map(({ value, icon }) => {
              const active = form.skills.includes(value);
              return (
                <button
                  key={value}
                  onClick={() => toggleSkill(value)}
                  className={`flex items-center gap-2.5 px-4 py-3.5 rounded-2xl border text-sm font-medium text-left transition-all ${
                    active
                      ? 'border-brand-purple bg-brand-purple/10 text-dark-text'
                      : 'border-dark-border bg-dark-surface text-dark-subtext'
                  }`}
                >
                  <span className="text-xl">{icon}</span>
                  <span className="leading-tight">{value}</span>
                  {active && (
                    <span className="ml-auto w-5 h-5 rounded-full bg-brand-purple flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Step 3: Bio ─────────────────────────────────────────────── */}
      {step === 3 && (
        <div className="flex-1 flex flex-col">
          <div className="mb-6">
            <span className="text-4xl mb-4 block">✍️</span>
            <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
              Tell clients about yourself
            </h1>
            <p className="text-dark-subtext text-sm">A short intro helps clients trust you and choose you.</p>
          </div>
          <textarea
            value={form.bio}
            onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            placeholder="e.g. I'm a professional photographer based in Kuwait with 5+ years of experience in fashion and commercial photography. I deliver high-quality visuals that help brands stand out."
            rows={6}
            className="w-full bg-dark-surface border border-dark-border rounded-2xl px-4 py-4 text-dark-text text-sm outline-none focus:border-brand-purple resize-none mb-2 leading-relaxed"
            autoFocus
          />
          <p className="text-dark-subtext text-xs">{form.bio.length} / 300 characters</p>
        </div>
      )}

      {/* ── Step 4: Location + Rate ─────────────────────────────────── */}
      {step === 4 && (
        <div className="flex-1 flex flex-col">
          <div className="mb-6">
            <span className="text-4xl mb-4 block">📍</span>
            <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
              Where are you based?
            </h1>
            <p className="text-dark-subtext text-sm">Clients nearby will find you more easily.</p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="relative">
              <select
                value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                className="w-full bg-dark-surface border border-dark-border rounded-2xl px-4 py-4 text-dark-text text-base outline-none focus:border-brand-purple appearance-none"
              >
                <option value="">Select your city...</option>
                {GCC_LOCATIONS.map(g => (
                  <optgroup key={g.country} label={g.country}>
                    {g.cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                ))}
              </select>
              <svg className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div>
              <label className="text-dark-subtext text-xs font-medium uppercase tracking-wide mb-2 block">
                Hourly rate (optional)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-subtext text-sm font-medium">KD</span>
                <input
                  type="number"
                  value={form.hourlyRate}
                  onChange={e => setForm(f => ({ ...f, hourlyRate: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full bg-dark-surface border border-dark-border rounded-2xl pl-10 pr-4 py-4 text-dark-text text-base outline-none focus:border-brand-purple"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 5: Profile Photo ───────────────────────────────────── */}
      {step === 5 && (
        <div className="flex-1 flex flex-col items-center">
          <div className="mb-8 text-center">
            <span className="text-4xl mb-4 block">🤳</span>
            <h1 className="text-2xl font-bold text-dark-text mb-2" style={{ letterSpacing: '-0.5px' }}>
              Add a profile photo
            </h1>
            <p className="text-dark-subtext text-sm">Profiles with photos get 3× more bookings.</p>
          </div>

          <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarPick} />

          <div className="relative mb-6">
            <button
              onClick={() => avatarInputRef.current?.click()}
              className="w-36 h-36 rounded-full border-4 border-dashed border-dark-border bg-dark-surface flex items-center justify-center overflow-hidden"
            >
              {form.avatarPreview ? (
                <img src={form.avatarPreview} className="w-full h-full object-cover" alt="" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-dark-subtext">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  <span className="text-xs font-medium">Tap to upload</span>
                </div>
              )}
            </button>
            <div className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-brand-purple flex items-center justify-center shadow-lg pointer-events-none">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </div>
          </div>

          <p className="text-dark-subtext text-xs text-center">
            {form.avatarPreview ? 'Tap to change' : 'JPG or PNG, max 10MB'}
          </p>

          {/* Summary */}
          <div className="mt-auto w-full bg-dark-surface border border-dark-border rounded-2xl p-4">
            <p className="text-dark-text text-sm font-semibold mb-2">Your profile summary</p>
            <div className="flex flex-col gap-1.5">
              {form.companyName && <p className="text-dark-subtext text-xs">🏢 {form.companyName}</p>}
              {form.skills.length > 0 && <p className="text-dark-subtext text-xs">🛠️ {form.skills.slice(0, 3).join(', ')}{form.skills.length > 3 ? ` +${form.skills.length - 3} more` : ''}</p>}
              {form.location && <p className="text-dark-subtext text-xs">📍 {form.location}</p>}
              {form.hourlyRate && <p className="text-dark-subtext text-xs">💰 KD {form.hourlyRate}/hr</p>}
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <div className="flex gap-3 mt-8">
        {step > 1 && (
          <button
            onClick={back}
            className="px-6 py-4 rounded-2xl bg-dark-surface border border-dark-border text-dark-text font-semibold text-sm"
          >
            Back
          </button>
        )}
        {step < TOTAL_STEPS ? (
          <button
            onClick={next}
            className="flex-1 py-4 rounded-2xl bg-brand-gradient text-white font-bold text-base"
          >
            Continue
          </button>
        ) : (
          <button
            onClick={handleFinish}
            disabled={saving}
            className="flex-1 py-4 rounded-2xl bg-brand-gradient text-white font-bold text-base disabled:opacity-60"
          >
            {saving ? 'Setting up...' : 'Finish Setup 🚀'}
          </button>
        )}
      </div>
    </div>
  );
}
